// src/components/onboarding/PolicyComplianceAdmin.jsx — Admin Policy Compliance Audit & Policy Version Manager
import React, { useEffect, useState } from "react";
import { Shield, FileText, CheckCircle2, AlertTriangle, RefreshCw, Search, Edit3 } from "lucide-react";
import api from "../../services/api";
import toast from "react-hot-toast";
import "./onboarding.css";

export default function PolicyComplianceAdmin() {
  const [records, setRecords] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [editForm, setEditForm] = useState({ version: "", title: "", summary: "", content: "" });
  const [savingPolicy, setSavingPolicy] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [recRes, polRes] = await Promise.all([
        api.get("/onboarding/admin/records"),
        api.get("/onboarding/policies")
      ]);
      const staffList = Array.isArray(recRes.data?.records)
        ? recRes.data.records
        : Array.isArray(recRes.data)
        ? recRes.data
        : [];
      setRecords(staffList);
      setPolicies(Array.isArray(polRes.data) ? polRes.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load compliance records");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredRecords = records.filter((r) => {
    const q = search.toLowerCase();
    return (
      (r.name && r.name.toLowerCase().includes(q)) ||
      (r.email && r.email.toLowerCase().includes(q)) ||
      (r.role && r.role.toLowerCase().includes(q))
    );
  });

  const isStaffCompliant = (r) => r.status === "Compliant & Verified" || r.onboardingCompleted === 1 || r.complianceStatus === "COMPLIANT";
  const isStaffOutdated = (r) => r.status === "Outdated Version" || r.isOutdated === true || r.complianceStatus === "OUTDATED";

  const totalStaff = records.length;
  const verifiedCount = records.filter(isStaffCompliant).length;
  const outdatedCount = records.filter(isStaffOutdated).length;
  const pendingCount = Math.max(0, totalStaff - verifiedCount - outdatedCount);

  const openEditPolicy = (p) => {
    setEditingPolicy(p);
    setEditForm({
      version: p.version || "1.0",
      title: p.title || "",
      summary: p.summary || "",
      content: p.content || ""
    });
  };

  const handleSavePolicy = async (e) => {
    e.preventDefault();
    if (!editForm.title || !editForm.content) {
      toast.error("Title and content are required");
      return;
    }

    setSavingPolicy(true);
    try {
      await api.put(`/onboarding/admin/policy/${editingPolicy.policy_key}`, editForm);
      toast.success("Policy updated successfully. Compliance cache refreshed.");
      setEditingPolicy(null);
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update policy");
    } finally {
      setSavingPolicy(false);
    }
  };

  return (
    <div className="crm-brand-page">
      {/* Overview Metric Pills */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        <span className="badge rounded-pill" style={{ background: "rgba(10,132,255,0.12)", color: "#0a84ff", border: "1px solid rgba(10,132,255,0.25)", padding: "6px 14px", fontSize: "12px", fontWeight: 600 }}>
          {totalStaff} Total Staff
        </span>
        <span className="badge rounded-pill" style={{ background: "rgba(48,209,88,0.12)", color: "#30d158", border: "1px solid rgba(48,209,88,0.3)", padding: "6px 14px", fontSize: "12px", fontWeight: 600 }}>
          {verifiedCount} Verified Compliant
        </span>
        {pendingCount > 0 && (
          <span className="badge rounded-pill" style={{ background: "rgba(255,159,10,0.12)", color: "#ff9f0a", border: "1px solid rgba(255,159,10,0.3)", padding: "6px 14px", fontSize: "12px", fontWeight: 600 }}>
            {pendingCount} Pending Onboarding
          </span>
        )}
        {outdatedCount > 0 && (
          <span className="badge rounded-pill" style={{ background: "rgba(255,69,58,0.12)", color: "#ff453a", border: "1px solid rgba(255,69,58,0.3)", padding: "6px 14px", fontSize: "12px", fontWeight: 600 }}>
            {outdatedCount} Outdated Version
          </span>
        )}
      </div>

      {/* Toolbar */}
      <div className="crm-brand-toolbar">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ position: "relative", width: 260 }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--muted)" }} />
            <input
              type="text"
              placeholder="Search staff member..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px 8px 32px",
                borderRadius: "8px",
                background: "var(--surface-2)",
                border: "1px solid var(--border)",
                color: "var(--text)",
                fontSize: "13px"
              }}
            />
          </div>

          <button
            type="button"
            className="crm-session-btn-refresh"
            onClick={loadData}
            disabled={loading}
          >
            <RefreshCw size={13} className={loading ? "spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Compliance Records Table */}
      <div className="compliance-admin-card mb-4">
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: "13px", fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.05em", display: "flex", alignItems: "center", gap: 6 }}>
            <Shield size={15} color="#0a84ff" /> Staff Policy Acceptance Ledger
          </span>
          <span style={{ fontSize: "12px", color: "var(--muted)" }}>Legally binding audit records</span>
        </div>

        <table className="tp-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Role</th>
              <th>Compliance Status</th>
              <th>Accepted Timestamp</th>
              <th>IP Address</th>
              <th style={{ textAlign: "right" }}>Policy Versions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [1, 2, 3].map((n) => (
                <tr key={n}>
                  <td><div className="crm-skeleton-pulse" style={{ width: 140, height: 16 }} /></td>
                  <td><div className="crm-skeleton-pulse" style={{ width: 70, height: 22, borderRadius: 12 }} /></td>
                  <td><div className="crm-skeleton-pulse" style={{ width: 110, height: 22, borderRadius: 12 }} /></td>
                  <td><div className="crm-skeleton-pulse" style={{ width: 130, height: 14 }} /></td>
                  <td><div className="crm-skeleton-pulse" style={{ width: 100, height: 14 }} /></td>
                  <td><div className="crm-skeleton-pulse" style={{ width: 80, height: 14, marginLeft: "auto" }} /></td>
                </tr>
              ))
            ) : filteredRecords.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: "center", padding: "32px", color: "var(--muted)" }}>
                  No employee compliance records match the search.
                </td>
              </tr>
            ) : (
              filteredRecords.map((rec) => {
                const compliant = isStaffCompliant(rec);
                const outdated = isStaffOutdated(rec);

                return (
                  <tr key={rec.employeeId || rec.id}>
                    <td>
                      <div style={{ fontWeight: 600, color: "var(--text)" }}>{rec.name}</div>
                      <div style={{ fontSize: "11.5px", color: "var(--muted)" }}>{rec.email}</div>
                    </td>
                    <td>
                      <span className="cell-badge">{rec.role}</span>
                    </td>
                    <td>
                      {compliant ? (
                        <span className="compliance-status-badge verified">
                          <CheckCircle2 size={12} /> Compliant &amp; Verified
                        </span>
                      ) : outdated ? (
                        <span className="compliance-status-badge outdated">
                          <AlertTriangle size={12} /> Outdated Version
                        </span>
                      ) : (
                        <span className="compliance-status-badge pending">
                          <AlertTriangle size={12} /> Pending Onboarding
                        </span>
                      )}
                    </td>
                    <td style={{ color: "var(--muted)", fontSize: "12px" }}>
                      {rec.acceptedAt || rec.accepted_at ? new Date(rec.acceptedAt || rec.accepted_at).toLocaleString() : "—"}
                    </td>
                    <td style={{ color: "var(--muted)", fontSize: "12px" }}>
                      <code>{rec.ipAddress || rec.ip_address || "—"}</code>
                    </td>
                    <td style={{ textAlign: "right", fontSize: "11.5px", color: "var(--muted)" }}>
                      {rec.termsVersion || rec.terms_version ? `v${rec.termsVersion || rec.terms_version}` : "Not Accepted"}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Policy Version & Content Manager */}
      <div className="compliance-admin-card">
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: "13px", fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.05em", display: "flex", alignItems: "center", gap: 6 }}>
            <FileText size={15} color="#0a84ff" /> Active Company Policies &amp; Version Governance
          </span>
          <span style={{ fontSize: "12px", color: "var(--muted)" }}>Updates require staff re-acceptance</span>
        </div>

        <div style={{ padding: "18px" }}>
          <div style={{ display: "grid", gap: 14 }}>
            {policies.map((p) => (
              <div
                key={p.policy_key}
                style={{
                  background: "var(--surface-2)",
                  border: "1px solid var(--border)",
                  borderRadius: 10,
                  padding: "14px 16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 12
                }}
              >
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontWeight: 600, color: "var(--text)", fontSize: "14px" }}>{p.title}</span>
                    <span className="policy-version-tag">v{p.version}</span>
                  </div>
                  <div style={{ fontSize: "12px", color: "var(--muted)", marginTop: 4, maxWidth: 650 }}>
                    {p.summary || p.content.slice(0, 120) + "..."}
                  </div>
                </div>

                <button
                  type="button"
                  className="crm-brand-btn-edit"
                  onClick={() => openEditPolicy(p)}
                  title="Edit policy text or update version"
                >
                  <Edit3 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Edit Policy Modal */}
      {editingPolicy && (
        <div className="crm-brand-modal-overlay">
          <div className="crm-brand-modal-shell" style={{ maxWidth: 640 }}>
            <div className="crm-brand-modal-head">
              <h4 style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: "var(--text)" }}>
                Edit Policy: {editingPolicy.title}
              </h4>
            </div>

            <form onSubmit={handleSavePolicy} style={{ padding: "20px" }}>
              <div style={{ display: "grid", gap: 14 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: 12 }}>
                  <div>
                    <label style={{ fontSize: "12px", fontWeight: 600, color: "var(--text)", marginBottom: 4, display: "block" }}>
                      Policy Title
                    </label>
                    <input
                      type="text"
                      value={editForm.title}
                      onChange={(e) => setEditForm((p) => ({ ...p, title: e.target.value }))}
                      style={{ width: "100%", padding: "8px 12px", borderRadius: 8, background: "var(--surface-2)", border: "1px solid var(--border)", color: "var(--text)", fontSize: "13px" }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: "12px", fontWeight: 600, color: "var(--text)", marginBottom: 4, display: "block" }}>
                      Version
                    </label>
                    <input
                      type="text"
                      value={editForm.version}
                      onChange={(e) => setEditForm((p) => ({ ...p, version: e.target.value }))}
                      placeholder="1.1"
                      style={{ width: "100%", padding: "8px 12px", borderRadius: 8, background: "var(--surface-2)", border: "1px solid var(--border)", color: "var(--text)", fontSize: "13px" }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: "12px", fontWeight: 600, color: "var(--text)", marginBottom: 4, display: "block" }}>
                    Executive Summary
                  </label>
                  <input
                    type="text"
                    value={editForm.summary}
                    onChange={(e) => setEditForm((p) => ({ ...p, summary: e.target.value }))}
                    style={{ width: "100%", padding: "8px 12px", borderRadius: 8, background: "var(--surface-2)", border: "1px solid var(--border)", color: "var(--text)", fontSize: "13px" }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: "12px", fontWeight: 600, color: "var(--text)", marginBottom: 4, display: "block" }}>
                    Full Legal Policy Content
                  </label>
                  <textarea
                    rows={8}
                    value={editForm.content}
                    onChange={(e) => setEditForm((p) => ({ ...p, content: e.target.value }))}
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 8, background: "var(--surface-2)", border: "1px solid var(--border)", color: "var(--text)", fontSize: "12.5px", lineHeight: 1.5 }}
                  />
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
                <button
                  type="button"
                  className="onboarding-btn-secondary"
                  onClick={() => setEditingPolicy(null)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="onboarding-btn-primary"
                  disabled={savingPolicy}
                >
                  {savingPolicy ? "Saving..." : "Save & Publish Version"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
